import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from '../service/api.service';
import { UserModel } from '../interfaces/UserModel';
import { SocketService } from '../service/socket.service';
import { AcuStorageService } from '../service/acustorage.service';


import { hasHrRole } from '../constant/roles';
@Injectable({
  providedIn: 'root'
})
export class UserStore {

  public userLogged: UserModel;
  public usersList: UserModel[] = [];

  public storeUpdated: EventEmitter<void> = new EventEmitter();

  constructor(
    private apiService: ApiService,
    private socketService: SocketService,
    private acuStorage: AcuStorageService
  ) {
    const savedUser = this.acuStorage.getStoredUser<UserModel>();
    if (savedUser) {
      this.userLogged = savedUser;
    }

    this.socketService.receiveUpdateUser().subscribe((payload) => {
      console.log("UserStore - receiveUpdateUser", payload);
      if (payload && (payload.id || payload._id)) {
        const payloadId = payload.id || payload._id;
        // Update the user in our local list if they exist
        const index = this.usersList?.findIndex(u => (u.id || u._id) == payloadId);
        if (index !== -1 && index !== undefined) {
          this.usersList[index] = Object.assign(this.usersList[index], payload);
          this.usersList = [...this.usersList];
        }

        // If it's NOT us, we don't need a full re-init, but we should emit update
        if (this.userLogged && payloadId != (this.userLogged.id || this.userLogged._id)) {
          this.storeUpdated.emit();
          return;
        }
      }
      this.initStore();
    });
    this.socketService.updateStoreReconnected.subscribe(() => {
      console.log("UserStore - updateStoreReconnected");
      if (this.userLogged) {
        this.initStore();
      }
    });
  }

  private isInitializing: boolean = false;

  public async initStore() {
    console.log('UserStore - initStore');
    if (!this.userLogged || this.isInitializing) return;

    this.isInitializing = true;
    try {
      let fetchedUsers: UserModel[] = [];
      const hasPrivileges = hasHrRole(this.userLogged.roles) || this.userLogged.roles?.includes('ADMIN');

      if (!hasPrivileges && this.userLogged.funzione && this.userLogged.funzione != 'UNKNOW') {
        const usersFunzione = await this.apiService.getUsersByFunzione(this.userLogged.funzione);
        const otherUsers = await this.apiService.getUsersOtherFunzione(this.userLogged.funzione);
        fetchedUsers = (usersFunzione || []).concat(otherUsers || []);
      }
      // Fallback or privileged view: fetch all
      if (fetchedUsers.length === 0) {
        fetchedUsers = await this.apiService.getAllUserPromise() || [];
      }

      // Filter out inactive / dismissed users (active === false)
      this.usersList = (fetchedUsers || []).filter(u => u.active !== false);

      if (this.userLogged && this.usersList.length > 0) {
        const currentUserId = this.userLogged.id || this.userLogged._id;
        const updatedSelf = this.usersList.find(user => (user.id || user._id) == currentUserId);
        if (updatedSelf) {
          // Keep local sediAttive if API has none (common for mock/new users)
          const localSedi = this.userLogged.sediAttive;
          const mergedUser = Object.assign({}, this.userLogged, updatedSelf);

          if (!mergedUser.sediAttive || mergedUser.sediAttive.length === 0) {
            mergedUser.sediAttive = localSedi;
          }

          this.userLogged = mergedUser;
          this.acuStorage.setStoredUser(this.userLogged);
        }
      }
      this.storeUpdated.emit();
    } catch (error) {
      console.error('UserStore initStore error:', error);
    } finally {
      this.isInitializing = false;
    }
  }

  logout() {
    this.userLogged = undefined;
    this.acuStorage.removeStoredUser();
    this.acuStorage.setJwtToken('');
  }

  private syncUserInList(user: UserModel, apiResult?: UserModel): UserModel {
    const merged = Object.assign({}, user, apiResult || {}, {
      funzione: user.funzione,
      roles: Array.isArray(user.roles) ? [...user.roles] : [],
      sediAttive: Array.isArray(user.sediAttive) ? [...user.sediAttive] : []
    });
    const targetId = merged.id || merged._id;
    if (targetId && this.usersList) {
      const idx = this.usersList.findIndex(u => (u.id || u._id) == targetId);
      if (idx !== -1) {
        this.usersList[idx] = Object.assign(this.usersList[idx], merged);
        this.usersList = [...this.usersList];
      }
    }
    return merged;
  }

  updateCurrentUser(user: UserModel): Promise<any> {
    return this.apiService.updateUser(user).then((res) => {
      const updated = this.syncUserInList(user, res);
      this.userLogged = Object.assign({}, updated);
      this.acuStorage.setStoredUser(this.userLogged);
      this.storeUpdated.emit();
    });
  }

  updateUser(user: UserModel): Promise<any> {
    return this.apiService.updateUser(user).then((res) => {
      const updated = this.syncUserInList(user, res);
      if (this.userLogged && (updated.id || updated._id) == (this.userLogged.id || this.userLogged._id)) {
        this.userLogged = Object.assign({}, updated);
        this.acuStorage.setStoredUser(this.userLogged);
      }
      this.storeUpdated.emit();
    });
  }

  async deleteUser(user: UserModel): Promise<any> {
    const userId = user?.id || user?._id;
    if (!userId) return;

    try {
      await this.apiService.deleteUser(userId);
    } catch (error: any) {
      // If backend returns 404, user is already deleted on server; proceed with local removal
      if (error?.status !== 404) {
        throw error;
      }
    }

    if (this.usersList) {
      this.usersList = this.usersList.filter(u => (u.id || u._id) !== userId);
    }
    this.storeUpdated.emit();
  }

  getUserById(id: string): UserModel {
    return this.usersList?.find(user => (user.id || user._id) == id);
  }

}
